import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Descope } from '@descope/react-sdk';
import { Redirect } from 'react-router-dom';
import { Button, Grid } from '@material-ui/core';

import { AmplifyAuthWidget, AuthLoader, oAuthStorage, signIn } from 'core/components/auth';
import { initializeFrontChatAndIdentify } from 'configure-services';
import CollectEmailScreen from 'core/components/auth/components/collect-email-screen/collect-email-screen';
import BaseDialog from 'core/components/base-dialog/base-dialog';
import Label from 'core/components/label/label';
import ErrorDialog from 'core/components/error-dialog/error-dialog';
import { useQueryParams } from 'core/hooks/useQueryParams';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import { capitalize } from 'core/helpers/formatters';
import { brandConstants } from 'brand-config';

import useStyles from './sign-in-screen.styles';

const descopeAuthEnabled = import.meta.env.VITE_DESCOPE_AUTH_ENABLED === '1';
const nonce = document.querySelector('meta[property="csp-nonce"]')?.getAttribute('content');

const AUTH_STEPS = {
  COLLECT_EMAIL: 'collect-email',
  DESCOPE_AUTH: 'descope-auth',
  COGNITO_AUTH: 'cognito-auth'
};

function SignInScreen() {
  const classes = useStyles();
  const queryParams = useQueryParams();
  const session = useSession();

  const [email, setEmail] = useState('');
  const [connectionExpired, setConnectionExpired] = useState(false);
  const [error, setError] = useState(null);
  const [descopeLoading, setDescopeLoading] = useState(true);

  const hasInitOAuth = oAuthStorage.getInitiatedAuthStatus();
  const assumeUser = queryParams.assumeUser === 'true';

  useEffect(() => {
    if (session.loading || session.isLoggedIn) {
      return;
    }
    if (assumeUser) {
      return;
    }
    if (oAuthStorage.isImpersonation()) {
      oAuthStorage.setImpersonation(false);
      window.location.reload();
    }
  }, [session.loading, session.isLoggedIn, assumeUser]);

  const [authStep, setAuthStep] = useState(() => {
    if (!descopeAuthEnabled) {
      return AUTH_STEPS.COGNITO_AUTH;
    }

    if (queryParams.code || queryParams.sso_app_id) {
      return AUTH_STEPS.DESCOPE_AUTH;
    }

    return AUTH_STEPS.COLLECT_EMAIL;
  });

  useEffect(() => {
    // initialize front chat only once when reaching sign in page for first time
    initializeFrontChatAndIdentify();

    if (descopeAuthEnabled && authStep === AUTH_STEPS.DESCOPE_AUTH) {
      // `signIn` function initializes the auth flow and saves the state in oAuthStorage storage.
      // If user is signing in with SSO, Descope will redirect back to /signin with `code` query param. In this case we don't need to re-initialize the auth flow.
      if (!hasInitOAuth && !queryParams.code) {
        oAuthStorage.setAuthEmail(email);

        signIn({ impersonation: assumeUser });
      }

      // OIDC connection between Descope and Cognito times out after 5 min.
      // Need to let the user know that the connection has expired and they need to start over.
      const signInTimeout = setTimeout(
        () => {
          setConnectionExpired(true);
          track('[Staff Sign in] Descope OIDC connection expired');
        },
        1000 * 60 * 4.9
      );

      return () => {
        clearTimeout(signInTimeout);
      };
    }
  }, [authStep]);

  if (session.isLoggedIn) {
    return <Redirect to="/" />;
  }

  if (error) {
    return (
      <ErrorDialog
        open
        onClose={() => {
          setError(null);
        }}
        title="Authentication Error"
        errors={[error]}
        label="Contact Support"
        // @TODO: Handle onClick
        onClick={() => {}}
      />
    );
  }

  if (connectionExpired) {
    return (
      <BaseDialog size="md" open>
        <Grid container direction="column">
          <Label type="greenBig" className={classes.title}>
            Your session has expired due to inactivity
          </Label>
          <Button
            onClick={() => {
              window.location.reload();
            }}
            className={classes.button}
            variant="contained"
            color="primary"
          >
            Return to sign in page
          </Button>
        </Grid>
      </BaseDialog>
    );
  }

  // 'code' is present in query params after SSO provider (like Okta) sends IdP response to Descope widget
  if ((queryParams.sso_app_id || queryParams.code) && authStep === AUTH_STEPS.DESCOPE_AUTH) {
    return (
      <>
        {descopeLoading && <AuthLoader />}
        <div className={classes.container}>
          <div className={classNames(classes.descopeWidget, { [classes.descopeWidgetLoading]: descopeLoading })}>
            <Descope
              flowId={
                assumeUser
                  ? import.meta.env.VITE_STAFF_DESCOPE_ASSUME_IDENTITY_FLOW_ID
                  : import.meta.env.VITE_STAFF_DESCOPE_FLOW_ID
              }
              form={{
                email: oAuthStorage.getAuthEmail() || '',
                brandName: capitalize(brandConstants.SHORT_NAME) || ''
              }}
              onError={(error) => {
                setError(error?.detail?.message);
              }}
              onReady={() => {
                setDescopeLoading(false);
              }}
              nonce={nonce}
            />
          </div>
        </div>
      </>
    );
  }

  if (authStep === AUTH_STEPS.COGNITO_AUTH) {
    return (
      <div className={classes.container}>
        <AmplifyAuthWidget
          email={email}
          handleEnterAnotherEmail={() => {
            setAuthStep(AUTH_STEPS.COLLECT_EMAIL);
            setEmail('');
          }}
        />
      </div>
    );
  }

  if (authStep === AUTH_STEPS.COLLECT_EMAIL) {
    return (
      <div className={classes.container}>
        <CollectEmailScreen
          email={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          onSubmit={async () => {
            setAuthStep(AUTH_STEPS.DESCOPE_AUTH);
          }}
        />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <AuthLoader />
    </div>
  );
}

export default SignInScreen;
